import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import {
	type AgentManagedExtensionPluginState,
	pluginKey,
} from '../../common/agentManagedExtensionPluginType';
import {
	getAgentManagedExtensionNodeView,
	rerenderAllNodeViews,
} from '../nodeviews/agentManagedExtensionNodeView';

import { createAutoOpenConfigOnInsertView } from './auto-open-view';
import { createSelectedWhileConfigOpenView } from './selected-while-config-open-view';
import {
	applyAutoCloseOnSelectionChange,
	applyAutoOpenOnInsert,
	applyMeta,
	getMeta,
	initialState,
} from './state';

export interface CreatePluginArgs {
	nodeViewPortalProviderAPI: PortalProviderAPI;
}

export const createPlugin = (
	args: CreatePluginArgs,
): SafePlugin<AgentManagedExtensionPluginState> =>
	new SafePlugin<AgentManagedExtensionPluginState>({
		key: pluginKey,
		props: {
			nodeViews: {
				bodiedExtension: getAgentManagedExtensionNodeView({
					nodeViewPortalProviderAPI: args.nodeViewPortalProviderAPI,
				}) as unknown as (
					node: PMNode,
					view: EditorView,
					getPos: () => number | undefined,
				) => NodeView,
			},
		},
		state: {
			init: initialState,
			apply(tr, prev, oldState, newState) {
				const withMeta = applyMeta(prev, getMeta(tr), newState);
				const withAutoOpen = applyAutoOpenOnInsert(withMeta, tr, oldState, newState);
				return applyAutoCloseOnSelectionChange(withAutoOpen, tr, newState);
			},
		},
		view: (editorView) => {
			const autoOpen = createAutoOpenConfigOnInsertView(editorView);
			const selectedWhileConfigOpen = createSelectedWhileConfigOpenView(editorView);
			let prevEditable = editorView.editable;

			return {
				destroy: () => {},
				update: (view) => {
					autoOpen.update();
					selectedWhileConfigOpen.update();

					if (view.editable !== prevEditable) {
						prevEditable = view.editable;
						rerenderAllNodeViews();
					}
				},
			};
		},
	});
